import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion, useInView } from 'framer-motion';
import {
    FaPython,
    FaDatabase,
    FaAws,
    FaDocker,
    FaGithub,
    FaMicrosoft,
    FaWindows
} from 'react-icons/fa';
import {
    SiApachespark,
    SiApacheairflow,
    SiDatabricks,
    SiPandas
} from 'react-icons/si';
// No additional icons from react-icons/si

const Skills = () => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, threshold: 0.1 });

    const fadeInVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 }
    };

    // Skills data based on your resume
    const skillCategories = [
        {
            name: "Languages",
            skills: [
                { name: "Python", icon: <FaPython /> },
                { name: "SQL", icon: <FaDatabase /> }
            ]
        },
        {
            name: "Data Engineering & ETL",
            skills: [
                { name: "Apache Spark", icon: <SiApachespark /> },
                { name: "PySpark", icon: <SiApachespark /> },
                { name: "Apache Airflow", icon: <SiApacheairflow /> },
                { name: "Databricks", icon: <SiDatabricks /> },
                { name: "Delta Lake", icon: <SiDatabricks /> },
                { name: "Microsoft Fabric", icon: <FaMicrosoft /> },
                { name: "SQL CDC", icon: <FaDatabase /> }
            ]
        },
        {
            name: "Developer Tools",
            skills: [
                { name: "AWS Glue", icon: <FaAws /> },
                { name: "Azure Data Factory", icon: <FaWindows /> },
                { name: "Synapse Analytics", icon: <FaWindows /> },
                { name: "Airbyte", icon: <FaDatabase /> },
                { name: "CI/CD pipelines", icon: <FaGithub /> },
                { name: "Git", icon: <FaGithub /> },
                { name: "Docker", icon: <FaDocker /> }
            ]
        },
        {
            name: "Libraries",
            skills: [
                { name: "Scrapy", icon: <FaPython /> },
                { name: "Pandas", icon: <SiPandas /> },
                { name: "NumPy", icon: <FaPython /> }
            ]
        },
        {
            name: "Cloud Platforms",
            skills: [
                { name: "AWS", icon: <FaAws /> },
                { name: "Azure", icon: <FaWindows /> },
                { name: "Fabric", icon: <FaMicrosoft /> }
            ]
        },
        {
            name: "Data Visualization & BI",
            skills: [
                { name: "Apache Superset", icon: <SiApachespark /> },
                { name: "Power BI", icon: <FaMicrosoft /> }
            ]
        },
        {
            name: "Data Streaming & Processing",
            skills: [
                { name: "Spark Streaming", icon: <SiApachespark /> },
                { name: "SQL Server CDC", icon: <FaDatabase /> },
                { name: "Fabric Eventstream", icon: <FaMicrosoft /> },
                { name: "Delta Lake", icon: <SiDatabricks /> }
            ]
        }
    ];

    return (
        <SkillsSection id="skills" ref={ref}>
            <div className="container">
                <SectionHeader
                    variants={fadeInVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                    transition={{ duration: 0.6 }}
                >
                    <h2>Skills</h2>
                    <Divider />
                </SectionHeader>

                <SkillsContainer>
                    {skillCategories.map((category, index) => (
                        <SkillCategory
                            key={index}
                            variants={fadeInVariants}
                            initial="hidden"
                            animate={isInView ? "visible" : "hidden"}
                            transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                        >
                            <CategoryName>{category.name}</CategoryName>
                            <SkillsGrid>
                                {category.skills.map((skill, skillIndex) => (
                                    <SkillItem key={skillIndex}>
                                        <SkillIcon>{skill.icon}</SkillIcon>
                                        <SkillName>{skill.name}</SkillName>
                                    </SkillItem>
                                ))}
                            </SkillsGrid>
                        </SkillCategory>
                    ))}
                </SkillsContainer>

                <CertificationsSection
                    variants={fadeInVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                    transition={{ duration: 0.6, delay: 0.2 + skillCategories.length * 0.1 }}
                >
                    <h3>Certifications</h3>
                    <CertificationsList>
                        <CertificationItem>
                            <CertificationName>
                                <a href="https://learn.microsoft.com/en-us/users/amritprasadphuyal-7226/credentials/9d5f07dad175e16c" target="_blank" rel="noopener noreferrer">
                                    Microsoft Certified: Fabric Data Engineer Associate (DP-700)
                                </a>
                            </CertificationName>
                            <CertificationIssuer>Microsoft, 2025</CertificationIssuer>
                        </CertificationItem>
                        <CertificationItem>
                            <CertificationName>
                                <a href="https://credentials.databricks.com/85f7e607-4e0f-44e4-b58b-bc54a0cf94b2" target="_blank" rel="noopener noreferrer">
                                    Databricks Certified Data Engineer Associate
                                </a>
                            </CertificationName>
                            <CertificationIssuer>Databricks, 2025</CertificationIssuer>
                        </CertificationItem>
                        <CertificationItem>
                            <CertificationName>
                                <a href="https://learn.microsoft.com/en-us/users/amritprasadphuyal-7226/credentials/7ebf7fe593cd4c58" target="_blank" rel="noopener noreferrer">
                                    Fabric Analytics Engineer Associate (DP-600)
                                </a>
                            </CertificationName>
                            <CertificationIssuer>Microsoft, 2025</CertificationIssuer>
                        </CertificationItem>
                        <CertificationItem>
                            <CertificationName>
                                <a href="https://www.credly.com/badges/5fcf6f1b-3d8e-4590-9560-d045f80e8d0f" target="_blank" rel="noopener noreferrer">
                                    Apache Airflow Fundamentals
                                </a>
                            </CertificationName>
                            <CertificationIssuer>Astronomer, 2024</CertificationIssuer>
                        </CertificationItem>
                        <CertificationItem>
                            <CertificationName>
                                <a href="https://www.credly.com/users/amrit-prasad-phuyal" target="_blank" rel="noopener noreferrer">
                                    Azure Fundamentals (AZ-900)
                                </a>
                            </CertificationName>
                            <CertificationIssuer>Microsoft, 2021</CertificationIssuer>
                        </CertificationItem>
                    </CertificationsList>
                </CertificationsSection>
            </div>
        </SkillsSection>
    );
};

// Styled Components
const SkillsSection = styled.section`
    padding: 100px 0;
    background-color: var(--primary);

    .container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 2rem;
    }
`;

const SectionHeader = styled(motion.div)`
    margin-bottom: 60px;
    text-align: center;

    h2 {
        font-size: 2.5rem;
        color: var(--text-light);
        margin-bottom: 1rem;
        position: relative;
        display: inline-block;
    }
`;

const Divider = styled.div`
    height: 4px;
    width: 70px;
    background-color: var(--accent);
    margin: 0 auto;
`;

const SkillsContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 40px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const SkillCategory = styled(motion.div)`
    background-color: var(--secondary);
    border-radius: 6px;
    padding: 25px;
    box-shadow: 0 10px 30px -15px var(--shadow);
    transition: transform 0.3s ease;

    &:hover {
        transform: translateY(-5px);
    }
`;

const CategoryName = styled.h3`
    color: var(--text-light);
    font-size: 1.3rem;
    padding-bottom: 10px;
    margin-bottom: 20px;
    border-bottom: 1px dashed rgba(30, 136, 229, 0.3);
`;

const SkillsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 15px;
`;

const SkillItem = styled.div`
    display: flex;
    align-items: center;
    flex-direction: column;
    padding: 10px;
    border-radius: 4px;
    background-color: rgba(30, 136, 229, 0.05);
    transition: all 0.3s ease;

    &:hover {
        background-color: rgba(30, 136, 229, 0.1);
        transform: translateY(-2px);
    }
`;

const SkillIcon = styled.div`
    font-size: 1.8rem;
    color: var(--accent);
    margin-bottom: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    
    svg {
        width: 1.8rem;
        height: 1.8rem;
        color: var(--accent);
        fill: currentColor;
    }
`;

const SkillName = styled.span`
    color: var(--text-secondary);
    font-size: 0.9rem;
    text-align: center;
`;

const CertificationsSection = styled(motion.div)`
    margin-top: 60px;
    background-color: var(--secondary);
    border-radius: 6px;
    padding: 30px;
    box-shadow: 0 10px 30px -15px var(--shadow);

    h3 {
        color: var(--text-light);
        font-size: 1.8rem;
        margin-bottom: 20px;
        padding-bottom: 10px;
        border-bottom: 1px dashed rgba(30, 136, 229, 0.3);
    }
`;

const CertificationsList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const CertificationItem = styled.div`
    padding: 15px;
    background-color: rgba(30, 136, 229, 0.05);
    border-radius: 4px;
    transition: all 0.3s ease;

    &:hover {
        background-color: rgba(30, 136, 229, 0.1);
        transform: translateY(-2px);
    }
`;

const CertificationName = styled.h4`
    font-size: 1.1rem;
    margin-bottom: 5px;
    
    a {
        color: var(--accent);
        text-decoration: none;
        
        &:hover {
            text-decoration: underline;
        }
    }
`;

const CertificationIssuer = styled.p`
    color: var(--text-secondary);
    font-size: 0.9rem;
`;

export default Skills;
